import { apiGet, apiGetMeta, apiPost } from './api-client.js';

/**
 * Typed wrappers over API_CONTRACT.md §13.6's appointment endpoints (`internal-api`'s
 * `appointment` module — `appointment-routes.ts`, read directly off the running server
 * because `VERIFIED_RESPONSES.md` has no entry for this module yet).
 *
 * **Two amendments the running server carries that the contract's prose does not, both
 * confirmed live rather than assumed:**
 *
 * 1. The permissions are `appointment.inbox` (list/detail/accept/reschedule/decline/
 *    cancel) and `appointment.verify` (entrance scan + complete) — §13.6 names
 *    `appointment.read`/`appointment.decide`, which do not exist in the seeded
 *    authorization matrix at all (`appointment-routes.ts`'s own file header explains why:
 *    an unmatrixed permission is refused for everybody). `officer`/`officer2` hold the
 *    first; `adminhr` holds both, which is why HR is the practical stand-in for a front
 *    desk (SCREEN_SPECS.md I15's own flagged gap — no dedicated reception role exists).
 * 2. `accept`/`reschedule` take `{scheduledStart, expectedVersion, reason?}` — **no
 *    `scheduledEnd`**. The window length is fixed by the slot policy, so a client-supplied
 *    end would be an unenforced field (confirmed: a body carrying `scheduledEnd` is
 *    rejected as an `unknown_field`, matching the exact reasoning
 *    `appointment/intake/index.ts` already documents for the PUBLIC intake schema).
 */

export interface BilingualLabel {
  labelNe: string;
  labelEn: string;
}

export interface AppointmentStatusInfo extends BilingualLabel {
  code: string;
}

export interface AppointmentDeclineInfo {
  reasonCode: string;
  /** The officer's own words. Internal only — never shown to the citizen (§13.6). */
  reason: string;
}

export interface AppointmentAvailabilityHint {
  userId: string;
  statusCode: string;
  labelNe: string;
  labelEn: string;
  isAvailableHint: boolean;
}

export interface AppointmentRow {
  id: string;
  referenceNo: string;
  visitor: { name: string; phone: string };
  purpose: string;
  targetUserId: string | null;
  targetOrgUnitId: string | null;
  requestedStart: string;
  requestedEnd: string;
  requestedDateBs: string;
  scheduledStart: string | null;
  scheduledEnd: string | null;
  scheduledDateBs: string | null;
  status: AppointmentStatusInfo;
  decline: AppointmentDeclineInfo | null;
  source: string;
  /** Optimistic-concurrency token — every accept/reschedule/decline/cancel echoes this back as `expectedVersion`. */
  version: number;
  /**
   * The target officer's OWN current availability, alongside the request
   * (REQUIREMENTS.md §3.3) — `null` when the request targets a section rather than a
   * named officer.
   */
  targetAvailability: AppointmentAvailabilityHint | null;
}

export interface AppointmentTimelineEvent {
  seqNo: number;
  fromStatus: string | null;
  toStatus: string;
  actorUserId: string | null;
  actorKind: string;
  reason: string | null;
  occurredAt: string;
  occurredDateBs: string;
}

export interface AppointmentDetailRow extends AppointmentRow {
  timeline: AppointmentTimelineEvent[];
}

export interface ListAppointmentsParams {
  page?: number;
  pageSize?: number;
  statusCode?: string;
  targetUserId?: string;
  targetOrgUnitId?: string;
  dateFrom?: string;
  dateTo?: string;
}

export interface OffsetPageMeta {
  page: number;
  pageSize: number;
  totalItems: number;
  totalPages: number;
}

export async function listAppointments(
  params: ListAppointmentsParams,
  signal?: AbortSignal,
): Promise<{ rows: AppointmentRow[]; page: OffsetPageMeta }> {
  const usp = new URLSearchParams();
  if (params.page !== undefined) usp.set('page', String(params.page));
  if (params.pageSize !== undefined) usp.set('pageSize', String(params.pageSize));
  if (params.statusCode) usp.set('statusCode', params.statusCode);
  if (params.targetUserId) usp.set('targetUserId', params.targetUserId);
  if (params.targetOrgUnitId) usp.set('targetOrgUnitId', params.targetOrgUnitId);
  if (params.dateFrom) usp.set('dateFrom', params.dateFrom);
  if (params.dateTo) usp.set('dateTo', params.dateTo);
  const qs = usp.toString();
  const { data, meta } = await apiGetMeta<AppointmentRow[]>(`/appointments${qs ? `?${qs}` : ''}`, signal);
  const page = (meta?.page as OffsetPageMeta | undefined) ?? { page: 1, pageSize: 25, totalItems: data.length, totalPages: 1 };
  return { rows: data, page };
}

export function getAppointment(appointmentId: string, signal?: AbortSignal): Promise<AppointmentDetailRow> {
  return apiGet<AppointmentDetailRow>(`/appointments/${appointmentId}`, signal);
}

export interface ScheduleInput {
  scheduledStart: string;
  expectedVersion: number;
  /** Optional context for a RESCHEDULE only — not accepted (and not needed) on first `accept`. */
  reason?: string;
}

export function acceptAppointment(appointmentId: string, input: ScheduleInput): Promise<AppointmentRow> {
  return apiPost<AppointmentRow>(`/appointments/${appointmentId}/accept`, input);
}

export function rescheduleAppointment(appointmentId: string, input: ScheduleInput): Promise<AppointmentRow> {
  return apiPost<AppointmentRow>(`/appointments/${appointmentId}/reschedule`, input);
}

export interface DeclineInput {
  reason: string;
  reasonCode: string;
  expectedVersion: number;
}

export function declineAppointment(appointmentId: string, input: DeclineInput): Promise<AppointmentRow> {
  return apiPost<AppointmentRow>(`/appointments/${appointmentId}/decline`, input);
}

export interface CancelInput {
  expectedVersion: number;
  reason?: string;
}

export function cancelAppointment(appointmentId: string, input: CancelInput): Promise<AppointmentRow> {
  return apiPost<AppointmentRow>(`/appointments/${appointmentId}/cancel`, input);
}

export type VerifyOutcomeCode = 'valid' | 'already_used' | 'invalid';

export interface VerifyInput {
  referenceNo?: string;
  qrToken?: string;
}

export interface VerifyResult {
  outcome: 'valid';
  appointment: AppointmentRow;
}

/**
 * `POST /appointments/verify` — AC-26's three OUTCOMES are three different HTTP shapes,
 * not one response the client branches on:
 *   - `valid`         → `200`, `{outcome: 'valid', appointment}` (the appointment is
 *                        already transitioned to `arrived` server-side by this call).
 *   - `already_used`  → `409 CONFLICT_STATE`, thrown as `ApiError` by `api-client.ts`.
 *   - `invalid`       → `404 NOT_FOUND`, thrown as `ApiError`.
 * This function returns the success shape and lets the two failure outcomes propagate as
 * `ApiError` — the caller (`EntranceVerificationPage`) branches on `error.code`, exactly
 * the discipline every other screen in this app uses, rather than a duck-typed union.
 */
export function verifyAppointment(input: VerifyInput): Promise<VerifyResult> {
  return apiPost<VerifyResult>('/appointments/verify', input);
}

export function completeAppointment(appointmentId: string, expectedVersion: number): Promise<AppointmentRow> {
  return apiPost<AppointmentRow>(`/appointments/${appointmentId}/complete`, { expectedVersion });
}

export interface AppointmentDeclineReason extends BilingualLabel {
  code: string;
  isSlotConflict: boolean;
  /** What a CITIZEN is told (A-050) — shown in this UI only as a preview, never edited here. */
  publicLabelNe: string;
  publicLabelEn: string;
}

export interface AppointmentPolicy {
  slotMinutes: number;
  leadTimeHours: number;
  horizonDays: number;
  maxPendingPerPhone: number;
  autoExpireAfterHours: number;
  version: number;
  declineReasons: AppointmentDeclineReason[];
  statuses: (AppointmentStatusInfo & { isTerminal: boolean; blocksSlot: boolean })[];
}

export function getAppointmentPolicy(signal?: AbortSignal): Promise<AppointmentPolicy> {
  return apiGet<AppointmentPolicy>('/appointment-policy', signal);
}

export { ApiError, NetworkError } from './api-client.js';
